import { PlusCircle } from "@phosphor-icons/react";
import { DropdownMenuTrigger } from "@radix-ui/react-dropdown-menu";
import { useState } from "react";

import { TEMPLATE_TYPES } from "features/templates/constants";
import useTemplates from "features/templates/datahooks/useTemplates";
import type { CreatedTemplate, TemplateType } from "features/templates/types";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuFooter,
  DropdownMenuItem,
  DropdownMenuList,
} from "common/components/ui/DropdownMenu";
import SelectButton from "common/components/ui/SelectButton";

import { useCampaignFlowContext } from "../../../../context/CampaignFlowContext";
import NewTemplate from "./NewTemplate";

import type { FlowTemplate } from "../../../../types";

export default function TemplateSelect({
  isEditing,
  selectTemplate,
  flowTemplate,
  isMessageFlow,
}: {
  isEditing: boolean;
  selectTemplate: (createdTemplate: CreatedTemplate) => void;
  flowTemplate: FlowTemplate;
  isMessageFlow: boolean;
}) {
  const { isFlowError, canEditFlow } = useCampaignFlowContext();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const { allTemplates, isLoadingAllTemplates } = useTemplates();

  const {
    original_id: selectedTemplateId,
    type,
    edited: isEdited,
  } = flowTemplate;

  const isDisabled = !canEditFlow || isEditing;
  const allowedTypes: TemplateType[] = isMessageFlow
    ? ["VOICE_TEMPLATE", "MESSAGE_TEMPLATE"]
    : [type];

  const filteredOptions = allTemplates
    ? allTemplates.filter((option) => {
        return (
          allowedTypes.includes(option.type) && option.id !== selectedTemplateId
        );
      })
    : [];

  const value = allTemplates?.find(({ id }) => id === selectedTemplateId);

  let selectButtonContent = "Choose template";
  if (isLoadingAllTemplates) {
    selectButtonContent = "Loading templates...";
  } else if (value) {
    selectButtonContent = value.name;
    if (isEdited) {
      selectButtonContent = `${value.name} (Edited)`;
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          asChild
          disabled={isLoadingAllTemplates || isDisabled}
        >
          <SelectButton
            className="flex-1"
            hasError={isFlowError}
            hasSelection={!!value}
            isLoading={isLoadingAllTemplates}
          >
            {selectButtonContent}
          </SelectButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-[var(--radix-popper-anchor-width)]">
          <DropdownMenuList>
            {filteredOptions.map((option) => {
              const optionTitle =
                option.id === selectedTemplateId && isEdited
                  ? `${option.name} (Edited)`
                  : option.name;

              const { icon: Icon } = TEMPLATE_TYPES[option.type];

              return (
                <DropdownMenuItem
                  key={option.id}
                  onSelect={() => selectTemplate(option)}
                >
                  <Icon />
                  <div className="flex flex-col gap-0.5">
                    <h5 className="line-clamp-1 text-left">{optionTitle}</h5>
                    {option.type !== "VOICE_TEMPLATE" && (
                      <p className="line-clamp-1 break-all text-left text-xs text-black-500">
                        {option.message}
                      </p>
                    )}
                  </div>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuList>
          <DropdownMenuFooter>
            <DropdownMenuItem
              onSelect={() => {
                setIsCreateDialogOpen(true);
              }}
            >
              <PlusCircle weight="fill" />
              Create new template
            </DropdownMenuItem>
          </DropdownMenuFooter>
        </DropdownMenuContent>
      </DropdownMenu>
      <NewTemplate
        isOpen={isCreateDialogOpen}
        setIsOpen={setIsCreateDialogOpen}
        typeOptions={allowedTypes}
        onCreated={(createdTemplate) => {
          selectTemplate(createdTemplate);
          setIsCreateDialogOpen(false);
        }}
      />
    </>
  );
}
